import { useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { PageSkeleton } from '../ui/Skeleton';
import { EmptyState } from '../ui/EmptyState';
import { StatusBadge, Notice } from '../ui/Badge';
import { IconMoney } from '../icons';
import { money, balanceLabel } from '../money';

/**
 * What the customer sees.
 *
 * Before this, the product had no customer-facing surface at all: a shop emailed "you owe
 * $1,326" with no way to see the work behind it, no way to check what had already been paid,
 * and no way to pay. It is also why card payments could not be wired — there was nowhere to
 * send anybody.
 *
 * Outside every shell and every auth gate, like /join: the visitor has no account and never
 * will. The token in the URL is the whole credential, so this shows one invoice and nothing
 * that leads anywhere else.
 *
 * The tone is somebody's customer, not a user of software. It says what the work was, what
 * has been paid, what is left, and — while there is no card payment — how to sort it out with
 * a human. It does not show a Pay button that does nothing.
 */
export function PublicInvoicePage() {
  const { token } = useParams({ from: '/i/$token' });
  const trpc = useTRPC();
  const q = useQuery(trpc.public.invoice.queryOptions({ token }));

  if (q.isPending) return <PageSkeleton />;
  if (q.isError || !q.data) {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <EmptyState
          icon={IconMoney}
          title="This link doesn't work"
          description="It may have been cancelled, or the address may be incomplete. Reply to the email it came from and whoever sent it can help."
        />
      </div>
    );
  }

  const inv = q.data;
  const settled = inv.status === 'paid' || inv.status === 'void';
  const overpaid = inv.balanceCents < 0;

  return (
    <main className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
      <header className="mb-6">
        <p className="text-sm text-muted-foreground">{inv.shopName}</p>
        <h1 className="mt-1 text-2xl font-semibold">{inv.number}</h1>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <StatusBadge
            tone={
              inv.status === 'paid'
                ? 'success'
                : inv.status === 'void'
                  ? 'neutral'
                  : 'warn'
            }
          >
            {inv.statusLabel}
          </StatusBadge>
          {inv.dueDate && !settled ? (
            <span className="text-sm text-muted-foreground">Due {inv.dueDate}</span>
          ) : null}
          {inv.poNumber ? (
            <span className="text-sm text-muted-foreground">PO {inv.poNumber}</span>
          ) : null}
        </div>
      </header>

      {inv.status === 'void' ? (
        <Notice tone="neutral">
          This invoice was cancelled. You don&apos;t owe anything on it.
        </Notice>
      ) : null}

      <section className="rounded-[var(--radius)] border border-border bg-surface">
        <div className="h-scroll">
          <table className="w-full min-w-[28rem] text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-2 font-semibold">What we did</th>
                <th className="px-4 py-2 text-right font-semibold">Qty</th>
                <th className="px-4 py-2 text-right font-semibold">Each</th>
                <th className="px-4 py-2 text-right font-semibold">Amount</th>
              </tr>
            </thead>
            <tbody>
              {inv.lines.map((l, i) =>
                l.adjustment ? (
                  // What came off the part above: indented, the amount only (12b).
                  <tr key={i} className="border-b border-border last:border-b-0">
                    <td
                      className="py-1.5 pl-8 pr-4 text-[var(--sw-success-text)]"
                      colSpan={3}
                    >
                      {l.name}
                    </td>
                    <td className="px-4 py-1.5 text-right tabular-nums text-[var(--sw-success-text)]">
                      {money(l.totalCents)}
                    </td>
                  </tr>
                ) : (
                  <tr key={i} className="border-b border-border last:border-b-0">
                    <td className="px-4 py-2.5">
                      <p className="font-medium">{l.name}</p>
                      {l.description ? (
                        <p className="text-xs text-muted-foreground">{l.description}</p>
                      ) : null}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{l.qty}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">
                      {money(l.unitPriceCents)}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">
                      {money(l.totalCents)}
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>

        <dl className="space-y-1 border-t border-border px-4 py-3 text-sm">
          <Total label="Subtotal" cents={inv.subtotalCents} />
          {inv.taxCents > 0 ? <Total label="Tax" cents={inv.taxCents} /> : null}
          {inv.lateFeeCents > 0 ? (
            <Total label="Late fee" cents={inv.lateFeeCents} />
          ) : null}
          <Total label="Total" cents={inv.totalCents + inv.lateFeeCents} strong />
          {inv.amountPaidCents > 0 ? (
            <Total label="Already paid" cents={-inv.amountPaidCents} />
          ) : null}
        </dl>

        <div className="flex items-center justify-between border-t border-border px-4 py-3">
          <span className="text-sm font-semibold">
            {overpaid ? 'Overpaid' : settled ? 'Balance' : 'Still owed'}
          </span>
          <span className="text-lg font-semibold tabular-nums">
            {balanceLabel(inv.balanceCents)}
          </span>
        </div>
      </section>

      {!settled && inv.balanceCents > 0 ? (
        <section className="mt-4 rounded-[var(--radius)] border border-border bg-surface p-4">
          <h2 className="text-sm font-semibold">How to pay</h2>
          {inv.canPayOnline ? null : (
            // No dead button. Card payment isn't wired, and pretending otherwise wastes
            // the time of the one person here who is actively trying to pay.
            <p className="mt-1 text-sm text-muted-foreground">
              {inv.shopName} isn&apos;t taking card payments through this page yet. Reply
              to the email this link came from to settle up, or use whatever you arranged
              with them.
            </p>
          )}
        </section>
      ) : null}

      <InvoiceProblemLink token={token} shopName={inv.shopName} status={inv.status} />

      <footer className="mt-6 text-xs text-muted-foreground">
        <p className="font-medium">{inv.shopName}</p>
        {/* A postal address is typed with line breaks in it and means them. */}
        {inv.shopAddress ? (
          <p className="whitespace-pre-line">{inv.shopAddress}</p>
        ) : null}
        {inv.issuedAt ? <p className="mt-1">Issued {inv.issuedAt}</p> : null}
      </footer>
    </main>
  );
}

/**
 * "Something not right?" (ops insights Phase 5b): a plain link to the server-rendered form at
 * `/i/:token/problem`, which works with no JavaScript. A full page load, not a router link: the
 * server answers that path, and the service worker leaves it alone (`lib/sw-routes.ts`).
 *
 * Not on a VOID invoice: the form refuses one ("This invoice was cancelled — reply to the shop's
 * email instead"), so offering it would only lead there.
 */
export function InvoiceProblemLink({
  token,
  shopName,
  status,
}: {
  token: string;
  shopName: string;
  status: string;
}) {
  if (status === 'void') return null;
  return (
    <p className="mt-4 text-sm">
      <a
        className="font-semibold text-[var(--sw-brand-text)] underline underline-offset-4 hover:no-underline focus-visible:outline focus-visible:outline-2"
        href={`/i/${encodeURIComponent(token)}/problem`}
      >
        Something not right? Tell {shopName}
      </a>
    </p>
  );
}

function Total({
  label,
  cents,
  strong,
}: {
  label: string;
  cents: number;
  strong?: boolean;
}) {
  return (
    <div className={`flex justify-between ${strong ? 'font-semibold' : ''}`}>
      <dt className={strong ? '' : 'text-muted-foreground'}>{label}</dt>
      <dd className="tabular-nums">{money(cents)}</dd>
    </div>
  );
}
